import api from './api';

export interface EmendaDTO {
  id?: string;
  numeroEmenda?: number; // Task-10
  exercicio?: number; // Task-10
  councilorId?: string;
  councilorName?: string; // From Councilor entity
  councilorPoliticalParty?: string; // From Councilor entity
  officialCode?: string;
  date?: string;
  value?: number;
  classification?: string;
  esfera?: string;
  existeConvenio?: boolean;
  numeroConvenio?: string;
  anoConvenio?: number;
  category?: string;
  /** Simplified external status — 5 values: Recebido, Iniciado, Em execução, Devolvido, Concluído. */
  status?: string;
  /** Detailed internal workflow state (all cycle-of-life states). */
  statusCicloVida?: string;
  institutionId?: string;
  institutionName?: string; // From Institution entity
  signedLink?: string;
  attachments?: string[];
  description?: string;
  objectDetail?: string;
  previsaoConclusao?: string;
  justificativa?: string;
  secretariaDestino?: string;
  atoNormativoTipo?: string;
  atoNormativoNumero?: string;
  atoNormativoAno?: number;
  atoNormativoUrl?: string;
  processoAdministrativoNumero?: string;
  tipoTransferencia?: string;
  classificacao?: string | number;
  funcaoCodigo?: string;
  dotacaoOrcamentariaId?: number;
  dotacaoOrcamentariaTexto?: string;
  dotacoes?: { dotacaoOrcamentariaId?: number; dotacaoOrcamentariaTexto?: string }[];
  funcionalidadeChave?: string;
}

const WORKFLOW_STATUS_LABELS = new Set([
  'Em aprovação',
  'Recebido',
  'Em análise de admissibilidade',
  'Admissibilidade aprovada',
  'Devolvida ao legislativo',
  'Em análise de demanda',
  'Análise de demanda aprovada',
  'Devolvida por incompatibilidade de demanda',
  'Em análise documental',
  'Análise documental aprovada',
  'Devolvida por inviabilidade documental',
  'Iniciado',
  'Em execução',
  'Concluído',
  'Devolvido',
]);

const normalizeStatusLabel = (value?: string | null): string => {
  if (!value) return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  const lower = trimmed.toLocaleLowerCase('pt-BR');

  for (const label of WORKFLOW_STATUS_LABELS) {
    if (label.toLocaleLowerCase('pt-BR') === lower) {
      return label;
    }
  }

  return trimmed;
};

/** Returns the detailed internal workflow status (statusCicloVida), used by workflow panels and queue logic. */
export const resolveEmendaStatus = (emenda?: Pick<EmendaDTO, 'status' | 'statusCicloVida'> | null): string => {
  const lifecycleStatus = normalizeStatusLabel(emenda?.statusCicloVida);
  const legacyStatus = normalizeStatusLabel(emenda?.status);

  if (!lifecycleStatus) return legacyStatus || 'Recebido';
  if (!legacyStatus) return lifecycleStatus;
  if (lifecycleStatus === legacyStatus) return lifecycleStatus;
  if (lifecycleStatus === 'Recebido' && legacyStatus !== 'Recebido') return legacyStatus;
  return lifecycleStatus;
};

/** Derives the 5-value simplified status from a detailed statusCicloVida label. */
export const deriveSimplifiedStatus = (detailedStatus?: string | null): string => {
  const s = (detailedStatus || '').trim().toLocaleLowerCase('pt-BR');
  if (s === 'em aprovação') return 'Em aprovação';
  if (!s || s === 'recebido') return 'Recebido';
  if (
    s === 'em análise de admissibilidade' ||
    s === 'admissibilidade aprovada' ||
    s === 'em análise de demanda' ||
    s === 'análise de demanda aprovada' ||
    s === 'em análise documental' ||
    s === 'análise documental aprovada'
  ) return 'Iniciado';
  if (s === 'iniciado' || s === 'em execução') return 'Em execução';
  if (
    s === 'devolvida ao legislativo' ||
    s === 'devolvida por incompatibilidade de demanda' ||
    s === 'devolvida por inviabilidade documental' ||
    s === 'devolvido'
  ) return 'Devolvido';
  if (s === 'concluído') return 'Concluído';
  return 'Recebido';
};

const normalizeWorkflowStatusKey = (value?: string | null): string =>
  String(value || '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

/** Mirrors the backend rule used when a new work plan is linked to an amendment. */
export const isEmendaElegivelParaNovoPlano = (emenda?: EmendaDTO | null): boolean => {
  if (!emenda) return false;

  const status = normalizeWorkflowStatusKey(emenda.statusCicloVida);
  const isDireta = String(emenda.tipoTransferencia || '').trim().toLocaleLowerCase('pt-BR') === 'direta';

  return status === (isDireta ? 'ANALISE_DEMANDA_APROVADA' : 'ANALISE_DOCUMENTAL_APROVADA');
};

export interface EmendaAcaoDTO {
  acao: 'APROVAR' | 'DEVOLVER' | 'REPROVAR' | 'SOLICITAR_APROVACAO' | 'AGUARDAR_DETALHAMENTO'
      | 'INICIAR_ANALISE' | 'APROVAR_ADMISSIBILIDADE' | 'REPROVAR_ADMISSIBILIDADE'
      | 'INICIAR_ANALISE_DEMANDA' | 'APROVAR_DEMANDA' | 'REPROVAR_DEMANDA'
      | 'INICIAR_ANALISE_DOCUMENTAL' | 'APROVAR_DOCUMENTAL' | 'REPROVAR_DOCUMENTAL'
      | 'DEVOLVER_CAMARA' | 'APROVAR_CAMARA';
  observacao?: string;
  usuario?: string;
  secretariaDestino?: string;
  tipoEmenda?: string;
  esfera?: string;
  dotacoes?: { dotacaoOrcamentariaId?: number; dotacaoOrcamentariaTexto?: string }[];
}

export interface EmendaHistoricoDTO {
  id: number;
  emendaId: string;
  acao: string;
  statusAnterior?: string;
  statusNovo?: string;
  observacao?: string;
  usuario?: string;
  dataHora: string;
  leiNumero?: string;
  leiDescricao?: string;
  dataRecebimentoOriginal?: string;
  dataRegistradaHistorico?: string;
  departamento?: string;
}

export interface EmendaAnexoDTO {
  id: string;
  emendaId: string;
  nomeOriginal: string;
  mimeType: string;
  tamanhoBytes: number;
  descricao?: string;
  usuarioUpload?: string;
  dataUpload?: string;
  viewUrl: string;
  isDespacho?: boolean;
  categoriaTaep?: string;
  descricaoPublica?: string;
  dataDocumento?: string;
  publicavel?: boolean;
  redacaoConfirmada?: boolean;
}

export interface EvidenciaTaepUpload {
  categoriaTaep: string;
  descricaoPublica: string;
  dataDocumento: string;
  publicavel: boolean;
  redacaoConfirmada: boolean;
}

function normalizeClassificacaoAsCode(payload: any) {
  if (!payload) return;
  if (payload.classificacao === undefined || payload.classificacao === null) return;
  // If it's already a number or numeric string, normalize to string '1'/'2'
  if (typeof payload.classificacao === 'number') {
    payload.classificacao = String(payload.classificacao);
    return;
  }
  if (typeof payload.classificacao === 'string') {
    const s = payload.classificacao.trim();
    if (/^\d+$/.test(s)) {
      payload.classificacao = s; // keep numeric string
      return;
    }
    const low = s.toLowerCase();
    if (low.includes('invest')) {
      payload.classificacao = '2';
      return;
    }
    if (low.includes('custe')) {
      payload.classificacao = '1';
      return;
    }
    // fallback: set to null so backend returns clear error
    payload.classificacao = null;
  }
}

export const emendaService = {
  listAnexos: async (emendaId: string): Promise<EmendaAnexoDTO[]> => {
    const response = await api.get(`/emendas/${emendaId}/anexos`);
    return response.data;
  },

  uploadAnexo: async (emendaId: string, file: File, descricao?: string, isDespacho?: boolean, evidencia?: EvidenciaTaepUpload): Promise<EmendaAnexoDTO> => {
    const formData = new FormData();
    formData.append('file', file);
    if (descricao) formData.append('descricao', descricao);
    if (typeof isDespacho === 'boolean') formData.append('isDespacho', String(isDespacho));
    if (evidencia) {
      formData.append('categoriaTaep', evidencia.categoriaTaep);
      formData.append('descricaoPublica', evidencia.descricaoPublica);
      formData.append('dataDocumento', evidencia.dataDocumento);
      formData.append('publicavel', String(evidencia.publicavel));
      formData.append('redacaoConfirmada', String(evidencia.redacaoConfirmada));
    }
    const response = await api.post(`/emendas/${emendaId}/anexos/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  deleteAnexo: async (emendaId: string, anexoId: string): Promise<void> => {
    await api.delete(`/emendas/${emendaId}/anexos/${anexoId}`);
  },

  list: async (): Promise<EmendaDTO[]> => {
    const response = await api.get('/emendas');
    return response.data;
  },

  listWithDetails: async (): Promise<EmendaDTO[]> => {
    try {
      const response = await api.get('/emendas/with-details');
      return response.data;
    } catch (error: any) {
      throw error;
    }
  },

  getById: async (id: string): Promise<EmendaDTO> => {
    const response = await api.get(`/emendas/${id}`);
    return response.data;
  },

  getByIdWithDetails: async (id: string): Promise<EmendaDTO> => {
    const response = await api.get(`/emendas/${id}/with-details`);
    return response.data;
  },

  alterarStatus: async (id: string, novoStatus: string, justificativa?: string): Promise<EmendaDTO> => {
    const response = await api.patch(`/emendas/${id}/status`, { novoStatus, justificativa });
    return response.data;
  },

  // Helper: normalize value to a numeric (Number) with two decimals acceptable by backend BigDecimal parsing
  // Accepts number (e.g. 1234.56) or Brazilian-formatted strings ("R$ 1.234,56" / "1.234,56")
  _normalizeValueForApi(value: any): any {
    if (value === undefined || value === null) return value;
    // If it's already a number, round to two decimals and return a Number
    if (typeof value === 'number' && !isNaN(value)) {
      return Number(value.toFixed(2));
    }
    if (typeof value === 'string') {
      // Remove currency symbol, spaces and thousands separators then replace comma with dot
      const cleaned = value.replace(/[R$\s]/g, '').replace(/\./g, '').replace(',', '.');
      const n = parseFloat(cleaned);
      if (isNaN(n)) return value;
      return Number(n.toFixed(2));
    }
    return value;
  },

  create: async (emenda: Partial<EmendaDTO>): Promise<EmendaDTO> => {
    const payload: any = { ...emenda };
    if (payload.value !== undefined) payload.value = emendaService._normalizeValueForApi(payload.value);
    // Normalize classificacao to numeric code before sending
    normalizeClassificacaoAsCode(payload);
    const response = await api.post('/emendas', payload);
    return response.data;
  },

  update: async (id: string, emenda: Partial<EmendaDTO>): Promise<EmendaDTO> => {
    const payload: any = { ...emenda };
    if (payload.value !== undefined) payload.value = emendaService._normalizeValueForApi(payload.value);
    // Normalize classificacao to numeric code before sending
    normalizeClassificacaoAsCode(payload);
    const response = await api.put(`/emendas/${id}`, payload);
    return response.data;
  },

  executarAcao: async (id: string, acao: EmendaAcaoDTO): Promise<EmendaDTO> => {
    const response = await api.post(`/emendas/${id}/acao`, acao);
    return response.data;
  },

  // Vincula a emenda a uma lei do cadastro de leis (null remove o vínculo).
  atualizarVinculoOrcamentario: async (id: string, leiOrcamentariaId: string | null): Promise<EmendaDTO> => {
    const response = await api.put(`/emendas/${id}/vinculo-orcamentario`, { leiOrcamentariaId });
    return response.data;
  },

  getHistorico: async (id: string): Promise<EmendaHistoricoDTO[]> => {
    const response = await api.get(`/emendas/${id}/historico`);
    return response.data;
  },

  registrarRecebidoCamara: async (
    id: string,
    payload: { leiNumero: string; leiDescricao: string; dataRecebimento: string }
  ): Promise<EmendaHistoricoDTO> => {
    const response = await api.post(`/emendas/${id}/recebido-camara`, payload);
    return response.data;
  },
};

export default emendaService;
